import type { AxePurchaseState, v2 } from '@deque/billing-service-client';
import { apiRequest } from '../../api-client';
import type { SubscriptionEnvironment } from './add-subscription';

export interface UpdateAdminSubscriptionParams {
  token: string;
  enterpriseId: string;
  subscriptionId: string;
  purchase_state?: AxePurchaseState;
  license_count?: number | null;
  unit_count?: number;
  start_date?: string;
  end_date?: string;
  trial_expires?: string | null;
  environment?: SubscriptionEnvironment;
  auto_add_users?: boolean;
  add_existing_users?: boolean;
  product_url?: string;
  customer_name?: string;
  customer_domain?: string;
}

const UPDATABLE_FIELDS: (keyof Omit<
  UpdateAdminSubscriptionParams,
  'token' | 'enterpriseId' | 'subscriptionId'
>)[] = [
  'purchase_state',
  'license_count',
  'unit_count',
  'start_date',
  'end_date',
  'trial_expires',
  'environment',
  'auto_add_users',
  'add_existing_users',
  'product_url',
  'customer_name',
  'customer_domain'
];

/**
 * Update an existing subscription for an enterprise.
 * PUT /api-internal/enterprise/:enterprise_id/subscriptions/:subscription_id
 */
export function updateAdminSubscription({
  token,
  enterpriseId,
  subscriptionId,
  ...params
}: UpdateAdminSubscriptionParams): Promise<v2.EnterpriseSubscription> {
  const body = Object.fromEntries(
    UPDATABLE_FIELDS.filter(field => {
      const value = params[field];
      return value !== undefined && value !== '';
    }).map(field => [field, params[field]])
  );

  // Omit license_count when null (no-license products) to avoid forwarding null to billing-service
  if (body.license_count === null) {
    delete body.license_count;
  }

  return apiRequest({
    method: 'PUT',
    endpoint: `/api-internal/enterprise/${enterpriseId}/subscriptions/${subscriptionId}`,
    token,
    body
  });
}
